import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CategoriesForm,
  ChangePassword,
  ChangePasswordToken,
  DataApiResponse,
  DeliveryAddress,
  Me,
  MePreferencesDto,
  MeVisibilityDto,
  Profile,
  ResearchRespondent,
  UserService,
  VacancyPreferencesModel,
} from '@shared/definitions';

function toPreferencesDto(prefs: VacancyPreferencesModel): MePreferencesDto {
  return {
    distance: prefs.distance,
    city: prefs.city,
    salary: prefs.salary,
    salary_period: prefs.salary_period,
    hours_range: prefs.hours_range,
    education_level: prefs.education_level,
    dream_professions:
      prefs.dream_professions?.map((dp) => ({
        ...(dp.id ? { id: dp.id } : {}),
        name: dp.name,
        position_id: dp.position_id,
        order: dp.order,
      })) ?? null,
  };
}

@Injectable({
  providedIn: 'root',
})
export class MeApiService {
  readonly BASE_HREF_ME = '/api/v1.0/me';

  constructor(private http: HttpClient) {}

  getMe = () => this.http.get<Me>(this.BASE_HREF_ME);

  updateProfile = (profileForm: Profile) => this.http.post(`${this.BASE_HREF_ME}/profile`, profileForm);

  updateAddress = (address: Partial<DeliveryAddress>) => this.http.post(`${this.BASE_HREF_ME}/address`, address);

  updatePassword = (passwordForm: ChangePassword) =>
    this.http.post(`${this.BASE_HREF_ME}/password-change`, passwordForm);

  changePasswordToken = (changePasswordToken: ChangePasswordToken, captchaHeaders?: HttpHeaders) =>
    this.http.post(`${this.BASE_HREF_ME}/password-change/token`, changePasswordToken, {
      headers: captchaHeaders || {},
    });

  resetPassword = (email: string, captchaHeaders?: HttpHeaders) =>
    this.http.post(`/api/v1.0/auth/password-reset`, { email }, { headers: captchaHeaders || {} });

  updatePhoto = (photoId: string) => this.http.post(`${this.BASE_HREF_ME}/photo`, { photo_id: photoId });

  updateInterests = (categoriesForm: CategoriesForm) =>
    this.http.post(`${this.BASE_HREF_ME}/subcategories`, categoriesForm);

  updateTerms = (terms: unknown) => this.http.post(`${this.BASE_HREF_ME}/agreements`, terms);

  getResearchRespondents(params: Record<string, string>) {
    return this.http.get<DataApiResponse<ResearchRespondent[]>>(`${this.BASE_HREF_ME}/research-respondents`, {
      params,
    });
  }

  getUserServices(params: { draw?: number; start?: number; length?: number }) {
    return this.http.get<DataApiResponse<UserService[]>>(`${this.BASE_HREF_ME}/services`, {
      params,
    });
  }

  updatePreferences = (prefs: VacancyPreferencesModel) =>
    this.http.post(`${this.BASE_HREF_ME}/preferences`, toPreferencesDto(prefs));

  updateVisibility = (dto: MeVisibilityDto) => this.http.post(`${this.BASE_HREF_ME}/visibility`, dto);

  deleteAccount = (password: string, captchaHeaders?: HttpHeaders) =>
    this.http.delete(this.BASE_HREF_ME, {
      body: { password },
      headers: captchaHeaders || {},
    });
}
